/** @jsx jsx */
/** @jsxFrag React.Fragment */
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import { customColors } from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import { LocationDescriptor } from 'history';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { UNIDENTIFIED_CLIENT } from 'src/app/graph/clientsPage/constants';
import { OperationIdBadge } from 'src/app/graph/operationsPage/OperationIdBadge';
import { CardWithHeader } from 'src/app/graph/operationsPage/report-pane/section-report/performance-section/serviceReport/CardWithHeader';
import { clientsRouteConfig, insightsRouteConfig } from 'src/app/graph/routes';
import { SparklineChart } from 'src/components/charts/SparklineChart';
import { MutationBadge } from 'src/components/common/operationTypeBadge/MutationBadge';
import { SubscriptionBadge } from 'src/components/common/operationTypeBadge/SubscriptionBadge';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { Metric } from 'src/components/report-metrics/metric/Metric';
import { useRouteParams } from 'src/hooks/useRouteParams';
import {
  TimeRangeOption,
  printTimeRangeLabel,
} from 'src/lib/config/timeRangeOptions';
import { format } from 'src/lib/format';
import { OperationType } from 'src/lib/graphqlTypes/types';
import { formatMetrics } from 'src/lib/metrics';
import {
  isOperationUnexecutable,
  unexecutableOperationReason,
} from 'src/lib/unexecutableOperationReason';

import {
  ClientTrafficSourceData,
  OperationTrafficSourceData,
  TrafficSourceSortMetric,
} from './types';

export const cardTitles = {
  fetchErrorPercentage: 'Error percentage',
  fetchCountPerMin: 'Fetch rate',
  serviceTimeP95Ms: 'P95 service time',
};

export const cardSubtitles = {
  clients: {
    fetchErrorPercentage:
      'Clients with the most errors when fetching this subgraph.',
    fetchCountPerMin:
      'Clients with the most fetches/min when fetching this subgraph.',
    serviceTimeP95Ms:
      'Clients with slowest P95 service times when fetching this subgraph.',
  },
  operations: {
    fetchErrorPercentage:
      'Operations with the most errors when fetching this subgraph.',
    fetchCountPerMin:
      'Operations with the most fetches/min when fetching this subgraph.',
    serviceTimeP95Ms:
      'Operations with the slowest P95 service times when fetching this subgraph.',
  },
};

export const cardListHeadings = {
  fetchErrorPercentage: 'Highest Error Percentages',
  fetchCountPerMin: 'Highest Request Rates',
  serviceTimeP95Ms: 'Slowest P95 Service Times',
};

export const cardNoDataMessages = {
  clients: {
    fetchErrorPercentage: 'No clients with errors',
    fetchCountPerMin: 'No clients fetching this subgraph',
    serviceTimeP95Ms: 'No clients fetching this subgraph',
  },
  operations: {
    fetchErrorPercentage: 'No operations with errors',
    fetchCountPerMin: 'No operations fetching this subgraph',
    serviceTimeP95Ms: 'No operations fetching this subgraph',
  },
};

export function TrafficSourceCard({
  currentValue,
  pastValue,
  sortMetric,
  timeRange,
  sparklineData,
  min,
  max,
  title,
  subtitle,
  listHeading,
  children,
}: {
  currentValue: number;
  pastValue: number | null;
  sortMetric: TrafficSourceSortMetric;
  timeRange: TimeRangeOption;
  sparklineData: number[];
  min: number;
  max: number;
  title: string;
  subtitle: string;
  listHeading: string;
  children: React.ReactNode;
}) {
  const [chartWidth, setChartWidth] = useState(250);
  const selfRef = useRef<null | HTMLDivElement>(null);
  useEffect(() => {
    const onResize = debounce(() => {
      if (!selfRef.current) {
        return;
      }
      setChartWidth(selfRef.current.offsetWidth);
    }, 50);

    window.addEventListener('resize', onResize);
    onResize();
    onResize.flush();
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const formattedMin = format.splitUnits(formatMetrics(sortMetric, min));
  const formattedMax = format.splitUnits(formatMetrics(sortMetric, max));
  const delta =
    // we can only compute the delta when we have a past value to compare to.
    pastValue !== null
      ? // if past value is 0, just show 100% delta.
        (100 * (currentValue - pastValue)) / (pastValue || currentValue || 1)
      : null;

  const lineColor =
    sortMetric === 'fetchErrorPercentage'
      ? customColors.border.error
      : sortMetric === 'serviceTimeP95Ms'
        ? customColors.border.beta
        : customColors.border.info;

  return (
    <CardWithHeader
      title={title}
      subTitle={subtitle}
      className="w-full"
      cardContent={
        <div>
          <div
            className="flex items-center justify-between"
            css={css`
              svg {
                fill: none;
                stroke: ${lineColor};
              }
            `}
          >
            <div className="flex-none">
              <Metric
                {...format.splitUnits(formatMetrics(sortMetric, currentValue))}
              />
              <div className="mt-1">{`Average for the ${printTimeRangeLabel(
                timeRange,
                {
                  custom: 'Custom Range',
                },
              )?.toLowerCase()}`}</div>
              <div>
                <span className="font-semibold">Range:</span>{' '}
                {formattedMin.value}
                {formattedMin.unit} - {formattedMax.value}
                {formattedMax.unit}
              </div>
              {delta !== null && (
                <div className="flex">
                  <span
                    className={classnames('mr-px', {
                      'text-green': delta >= 0,
                      'rotate-180 text-red': delta < 0,
                    })}
                  >
                    ▲
                  </span>
                  <Metric
                    className="mr-1"
                    nosize={true}
                    value={`${delta > 0 ? '+' : ''}${format.number(
                      delta,
                      '0.00',
                    )}`}
                    unit="%"
                  />
                  <span> since yesterday</span>
                </div>
              )}
            </div>
            <div ref={selfRef} className="ml-6 w-full flex-1 overflow-hidden">
              <SparklineChart
                width={chartWidth}
                height={60}
                margin={0}
                data={sparklineData}
              />
            </div>
          </div>

          <div className="mb-1 mt-4 text-sm font-semibold uppercase text-secondary">
            {listHeading}
          </div>

          {children}
        </div>
      }
    />
  );
}

export function OperationsTrafficSourceCard<T extends TrafficSourceSortMetric>({
  currentValue,
  pastValue,
  sortMetric,
  timeRange,
  sparklineData,
  min,
  max,
  topSources,
}: {
  currentValue: number;
  pastValue: number | null;
  sortMetric: T;
  timeRange: TimeRangeOption;
  sparklineData: number[];
  min: number;
  max: number;
  topSources: OperationTrafficSourceData<T>[];
}) {
  const location = useLocation();

  const title = cardTitles[sortMetric];
  const subtitle = cardSubtitles.operations[sortMetric];
  const listHeading = cardListHeadings[sortMetric];
  const noDataMessage = cardNoDataMessages.operations[sortMetric];

  const linkToOperation = useCallback(
    (source: OperationTrafficSourceData<T>) =>
      insightsRouteConfig.locationFrom({
        location,
        patch: {
          insightsTab: 'operations',
          query: source.operationId ?? '',
          queryName: source.operationName ?? '',
        },
      }),
    [location],
  );
  return (
    <TrafficSourceCard
      currentValue={currentValue}
      pastValue={pastValue}
      sortMetric={sortMetric}
      timeRange={timeRange}
      sparklineData={sparklineData}
      min={min}
      max={max}
      title={title}
      subtitle={subtitle}
      listHeading={listHeading}
    >
      {topSources.length > 0 ? (
        topSources.map((source) => {
          const metricValue = source[sortMetric];
          if (metricValue === undefined || metricValue === null) {
            return [];
          }
          return (
            <TrafficSourceCardOperationRow
              key={source.operationId}
              operationId={source.operationId}
              operationName={source.operationName}
              operationType={source.operationType}
              metric={metricValue}
              sortMetric={sortMetric}
              linkToOperation={linkToOperation(source)}
            />
          );
        })
      ) : (
        <span className="italic text-secondary">{noDataMessage}</span>
      )}
    </TrafficSourceCard>
  );
}

export function ClientsTrafficSourceCard<T extends TrafficSourceSortMetric>({
  currentValue,
  pastValue,
  sortMetric,
  timeRange,
  sparklineData,
  min,
  max,
  topSources,
}: {
  currentValue: number;
  pastValue: number | null;
  sortMetric: T;
  timeRange: TimeRangeOption;
  sparklineData: number[];
  min: number;
  max: number;
  topSources: ClientTrafficSourceData<T>[];
}) {
  const routeParams = useRouteParams(insightsRouteConfig);

  const title = cardTitles[sortMetric];
  const subtitle = cardSubtitles.clients[sortMetric];
  const listHeading = cardListHeadings[sortMetric];
  const noDataMessage = cardNoDataMessages.clients[sortMetric];

  const linkToClient = useCallback(
    (source: ClientTrafficSourceData<T>) =>
      clientsRouteConfig.location({
        graphId: routeParams.graphId,
        graphVariant: routeParams.graphVariant,
        clientName: source.clientName || UNIDENTIFIED_CLIENT,
        from: routeParams.from,
        to: routeParams.to,
        range: routeParams.range,
      }),
    [routeParams],
  );

  return (
    <TrafficSourceCard
      currentValue={currentValue}
      pastValue={pastValue}
      sortMetric={sortMetric}
      timeRange={timeRange}
      sparklineData={sparklineData}
      min={min}
      max={max}
      title={title}
      subtitle={subtitle}
      listHeading={listHeading}
    >
      {topSources.length > 0 ? (
        topSources.flatMap((source) => {
          const metricValue = source[sortMetric];
          if (metricValue === undefined || metricValue === null) {
            return [];
          }
          return [
            <TrafficSourceCardClientRow
              key={source.clientName}
              clientName={source.clientName}
              metric={metricValue}
              sortMetric={sortMetric}
              linkToClient={linkToClient(source)}
            />,
          ];
        })
      ) : (
        <span className="italic text-secondary">{noDataMessage}</span>
      )}
    </TrafficSourceCard>
  );
}

function TrafficSourceCardOperationRow({
  operationId,
  operationName,
  operationType,
  metric,
  sortMetric,
  linkToOperation,
}: {
  operationId: string | null;
  operationName: string | null;
  operationType: OperationType | null;
  metric: number | null;
  sortMetric: TrafficSourceSortMetric;
  linkToOperation: LocationDescriptor;
}) {
  return (
    <Link
      className="group -mx-2 flex cursor-pointer items-center rounded px-2 py-1.5 hover:bg-btn-secondary-hover"
      key={`source-${operationId}`}
      to={linkToOperation}
      data-testid="traffic-sources-card-operation-row"
    >
      <div className="mr-1 flex min-w-0 flex-1 items-center ">
        {isOperationUnexecutable(operationName ?? '') ? (
          <>
            <span
              title={operationName ?? ''}
              className="truncate italic text-secondary hover:text-black"
            >
              {operationName}
            </span>
            <Tooltip label={unexecutableOperationReason(operationName ?? '')}>
              <IconInfo className="ml-2 text-link" />
            </Tooltip>
          </>
        ) : (
          <>
            <span title={operationName ?? ''} className="truncate">
              {operationName}
            </span>
            {operationId && (
              <OperationIdBadge className="ml-1.5 mr-1" id={operationId} />
            )}
          </>
        )}
        {operationType === OperationType.MUTATION && <MutationBadge />}
        {operationType === OperationType.SUBSCRIPTION && <SubscriptionBadge />}
      </div>
      <Metric
        className="flex-none"
        nosize={true}
        {...format.splitUnits(formatMetrics(sortMetric, metric))}
      />
    </Link>
  );
}

function TrafficSourceCardClientRow({
  clientName,
  metric,
  sortMetric,
  linkToClient,
}: {
  clientName: string | null;
  metric: number | null;
  sortMetric: TrafficSourceSortMetric;
  linkToClient: LocationDescriptor;
}) {
  return (
    <Link
      className="group -mx-2 flex cursor-pointer items-center rounded px-2 py-1.5 hover:bg-btn-secondary-hover"
      key={`source-${clientName}`}
      to={linkToClient}
      data-testid="traffic-sources-card-client-row"
    >
      <div className="mr-1 flex min-w-0 flex-1 items-center ">
        <>
          <span
            title={clientName ?? 'unidentified clients'}
            className={classnames('truncate', {
              'italic text-secondary': !clientName,
            })}
          >
            {clientName || 'unidentified clients'}
          </span>
        </>
      </div>
      <Metric
        className="flex-none"
        nosize={true}
        {...format.splitUnits(formatMetrics(sortMetric, metric))}
      />
    </Link>
  );
}
